import _get from 'lodash/get'
import _size from 'lodash/size'
import { map as mapPromise, all as allPromise } from 'bluebird'
import {
  Map, List, Set, fromJS,
} from 'immutable'
import { get as apiGet, TYPE_BROOKLYN } from 'api-client'
import { getUserLanguagePrimary } from 'services/user'
import { getPortalCommunityPM } from 'services/portal/actions'
import { TYPE_PROMOTION } from 'services/pm-section'
import {
  getVideos, getSeries, getFaqs, getTerms, getPromotions,
} from 'services/node'

import {
  setPmList as setPmListAction,
  setPmListError as setPmListErrorAction,
  GET_MULTIPLE_PM_LISTS,
  GET_PM_LIST,
} from './actions'
import {
  PM_LIST_TYPE_CONTENT,
  PM_LIST_TYPE_CONTENT_PEOPLE,
  PM_LIST_TYPE_CONTENT_TAGS,
  PM_LIST_TYPE_PORTALS,
} from './types'

/**
 * Get multiple page manager lists and dispatch associated actions
 * @param {Object} options The options
 * @param {import('redux').Dispatch} options.dispatch Redux dispatch function
 * @param {String} options.language The request language
 * @param {Map} options.auth The auth state
 * @param {Map} options.pmList The page manager list state
 * @param {String[]|List|Set} options.listIds The page manager list ids
 */
export async function getPmListMultiple (options = {}) {
  const {
    dispatch,
    language,
    auth = Map(),
    pmList = Map(),
  } = options
  let { listIds = [] } = options
  if (List.isList(listIds) || Set.isSet(listIds)) {
    listIds = listIds.toArray()
  }
  try {
    await mapPromise(listIds, (listId) => getPmListSingle({
      dispatch,
      listId,
      language,
      auth,
      pmList,
    }))
  } catch (e) {
    // Do nothing
  }
}

/**
 * Get one page manger list and dispatch associated actions
 * @param {Object} options The options
 * @param {import('redux').Dispatch} options.dispatch Redux dispatch function
 * @param {Map} options.auth The auth state
 * @param {Map} options.pmList The page manager list state
 * @param {String} options.listId The page manager list id
 * @param {String} options.language The request language
 */
export async function getPmListSingle (options = {}) {
  const {
    dispatch,
    language,
    auth = Map(),
    listId,
    pmList = Map(),
  } = options
  try {
    const processing = pmList.getIn([listId, 'processing'])
    if (processing) {
      return
    }
    const response = await apiGet(`page-manager/list/${listId}`, { language }, { auth }, TYPE_BROOKLYN)
    const pmListData = fromJS(_get(response, 'body', {}))
    dispatch(setPmListAction(listId, pmListData))
    switch (pmListData.get('type')) {
      case PM_LIST_TYPE_CONTENT:
      case PM_LIST_TYPE_CONTENT_PEOPLE:
      case PM_LIST_TYPE_CONTENT_TAGS: {
        const listItems = pmListData.get('listItems') || List()
        const contentList = listItems.map((v) => Map({
          contentId: v.get('id'),
          contentType: v.get('type'),
        }))
        const contentPromises = []
        // Set makes the values unique
        const videos = contentList.filter((v) => v.get('contentType') === 'video').toSet()
        if (!videos.isEmpty()) {
          const ids = videos.map((v) => v.get('contentId')).toArray()
          dispatch(getVideos({ ids, language }))
        }
        // Set makes the values unique
        const series = contentList.filter((v) => v.get('contentType') === 'series').toSet()
        if (!series.isEmpty()) {
          const ids = series.map((v) => v.get('contentId')).toArray()
          dispatch(getSeries({ ids, language }))
        }

        // Set makes the values unique for type: 'faq'
        const faqs = contentList.filter((v) => v.get('contentType') === 'faq').toSet()
        if (!faqs.isEmpty()) {
          const ids = faqs.map((v) => v.get('contentId')).toArray()
          dispatch(getFaqs({ ids, language }))
        }

        // Set makes the values unique for type: 'promotion'
        const promotions = contentList.filter((v) => v.get('contentType') === TYPE_PROMOTION).toSet()
        if (!promotions.isEmpty()) {
          const ids = promotions.map((v) => v.get('contentId')).toArray()
          dispatch(getPromotions({ ids, language }))
        }

        if (_size(contentPromises) > 0) {
          await allPromise(contentPromises)
        }
        // Set makes the values unique
        const termTypes = List(['person', 'tag'])
        const terms = contentList.filter((v) => termTypes.includes(v.get('contentType'))).toSet()

        if (!terms.isEmpty()) {
          const ids = terms.map((v) => v.get('contentId')).toArray()
          const contentType = contentList.getIn([0, 'contentType'])
          dispatch(getTerms({
            ids, language, fetchAsNodes: true, contentType,
          }))
        }

        break
      }
      case PM_LIST_TYPE_PORTALS: {
        const listItems = pmListData.get('listItems').map((v) => v.get('uuid'))
        dispatch(getPortalCommunityPM(listItems))
        break
      }
      default:
        break
    }
  } catch (e) {
    dispatch(setPmListErrorAction(listId, e))
  }
}

/**
 * Redux middleware
 * @param {import('redux').Store} store A redux store
 * @returns {import('redux').Middleware} Redux middleware
 */
export default function middleware (store) {
  return (next) => (action) => {
    const { dispatch } = store
    const { auth = Map(), user = Map(), pmList = Map() } = store.getState()
    const language = getUserLanguagePrimary(user)
    switch (action.type) {
      case GET_MULTIPLE_PM_LISTS:
        getPmListMultiple({
          language,
          auth,
          dispatch,
          listIds: fromJS(action.payload),
          pmList,
        })
        break
      case GET_PM_LIST:
        getPmListSingle({
          language,
          auth,
          dispatch,
          listId: action.payload,
          pmList,
        })
        break
      default:
        break
    }
    next(action)
  }
}
